/**
 * Document browser, upload page and document detail with preview.
 */
import { ATTACHABLE_TYPES, DOCUMENT_CATEGORIES } from "@shared/constants.ts";
import { documentUpdateSchema } from "@shared/schemas/operations.ts";
import { Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router";
import { type DocumentRow, UploadControl } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { SelectField, TextAreaField, TextField } from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { FilterBar, FilterSelect, SearchInput } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  PageHeader,
  QueryState,
  Section,
} from "../../components/ui/kit.tsx";
import { api, type Params } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { formatBytes, useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useSortState, useUrlState } from "../../lib/url-state.ts";

const ENTITY_ROUTES: Record<string, string> = {
  property: "properties",
  tenancy: "tenancies",
  tenant: "tenants",
  income: "income",
  expense: "expenses",
  task: "tasks",
  maintenance: "maintenance",
  communication: "communications",
  compliance: "compliance",
  contractor: "contractors",
  loan: "finance",
  tax_owner: "owners",
};

export function DocumentTable({ filters }: { filters: Params }) {
  const f = useFormat();
  const { sort, onSort } = useSortState("date", "desc");
  const q = useApi<DocumentRow[]>("/api/documents", { ...filters, sort: sort.sort, dir: sort.dir });
  const columns: Column<DocumentRow>[] = [
    {
      key: "name",
      header: "Name",
      sort: "name",
      cell: (d) => (
        <>
          <Icon name="file" size={14} className="me-1 text-muted" />
          <span className="fw-semibold">{d.name}</span>
          <div className="small text-muted">{d.originalFilename}</div>
        </>
      ),
    },
    {
      key: "category",
      header: "Category",
      sort: "category",
      cell: (d) => f.label(DOCUMENT_CATEGORIES, d.category),
    },
    { key: "property", header: "Property", cell: (d) => d.addressLine1 ?? "—" },
    { key: "size", header: "Size", sort: "size", cell: (d) => formatBytes(d.sizeBytes) },
    { key: "date", header: "Uploaded", sort: "date", cell: (d) => f.date(d.createdAt) },
    {
      key: "dl",
      header: "",
      cell: (d) => (
        <a
          className="btn btn-sm btn-light"
          href={`/api/documents/${d.id}/content`}
          aria-label={`Download ${d.name}`}
        >
          <Icon name="download" size={14} />
        </a>
      ),
    },
  ];
  return (
    <QueryState query={q}>
      {(rows) => (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(d) => d.id}
          rowLink={(d) => `/documents/${d.id}`}
          sort={sort}
          onSort={onSort}
          empty={
            <EmptyState icon="file" title="No documents">
              Upload tenancy agreements, certificates, receipts, invoices and photos. Files are stored
              securely in object storage.
            </EmptyState>
          }
        />
      )}
    </QueryState>
  );
}

export function DocumentBrowserPage() {
  const { get } = useUrlState();
  const lookups = useLookups();
  const { can } = useAuth();
  const propertyId = get("propertyId");
  return (
    <>
      <PageHeader title="Documents" subtitle="All documents across your portfolio" />
      {can("documents:write") && (
        <Card className="mb-4">
          <Card.Body>
            <h2 className="h6">Upload documents{propertyId ? " to this property" : ""}</h2>
            <UploadControl
              entityType={propertyId ? "property" : undefined}
              entityId={propertyId || undefined}
            />
          </Card.Body>
        </Card>
      )}
      <Card>
        <FilterBar>
          <SearchInput placeholder="Search documents" />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
          <FilterSelect param="category" label="Category" options={DOCUMENT_CATEGORIES} />
        </FilterBar>
        <DocumentTable filters={{ propertyId, category: get("category"), q: get("q") }} />
      </Card>
    </>
  );
}

export function DocumentDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const lookups = useLookups();
  const { can } = useAuth();
  const q = useApi<DocumentRow>(`/api/documents/${id}`);
  return (
    <QueryState query={q}>
      {(d) => {
        const url = `/api/documents/${d.id}/content`;
        const isImage = d.mimeType.startsWith("image/") && d.mimeType !== "image/heic";
        const isPdf = d.mimeType === "application/pdf";
        return (
          <>
            <PageHeader
              title={d.name}
              subtitle={`${f.label(DOCUMENT_CATEGORIES, d.category)} · ${formatBytes(d.sizeBytes)} · uploaded ${f.date(d.createdAt)}${d.uploadedByName ? ` by ${d.uploadedByName}` : ""}`}
              breadcrumbs={[{ label: "Documents", to: "/documents" }, { label: d.name }]}
              actions={
                <>
                  <a className="btn btn-primary" href={url}>
                    <Icon name="download" size={16} /> Download
                  </a>
                  {can("documents:write") && (
                    <ModalFormButton
                      label="Rename / edit"
                      buttonSize="md"
                      title="Edit document"
                      schema={documentUpdateSchema}
                      defaults={{
                        name: d.name,
                        category: d.category,
                        propertyId: d.propertyId ?? "",
                        notes: d.notes,
                      }}
                      onSubmit={(v) => api.put(`/api/documents/${d.id}`, v)}
                    >
                      {() => (
                        <>
                          <TextField name="name" label="Name" required />
                          <SelectField name="category" label="Category" options={DOCUMENT_CATEGORIES} />
                          <SelectField
                            name="propertyId"
                            label="Property"
                            options={toOptions(lookups?.properties)}
                            placeholder="—"
                          />
                          <TextAreaField name="notes" label="Notes" rows={2} />
                        </>
                      )}
                    </ModalFormButton>
                  )}
                  <Can permission="documents:delete">
                    <ConfirmButton
                      size="md"
                      title="Delete this document?"
                      body="The file will be permanently removed from storage."
                      onConfirm={async () => {
                        await api.delete(`/api/documents/${d.id}`);
                        await invalidate();
                        navigate("/documents");
                      }}
                    >
                      Delete
                    </ConfirmButton>
                  </Can>
                </>
              }
            />
            <Row>
              <Col lg={8}>
                <Section title="Preview">
                  {isImage ? (
                    <img
                      src={`${url}?disposition=inline`}
                      alt={d.name}
                      className="img-fluid rounded border"
                    />
                  ) : isPdf ? (
                    <iframe
                      title={d.name}
                      src={`${url}?disposition=inline`}
                      style={{ width: "100%", height: "70vh", border: 0 }}
                    />
                  ) : (
                    <p className="text-muted mb-0">
                      Preview isn't available for this file type. Download it to view.
                    </p>
                  )}
                </Section>
              </Col>
              <Col lg={4}>
                <Section title="Details">
                  <DefinitionList
                    items={[
                      ["Original file", d.originalFilename],
                      ["Type", d.mimeType],
                      [
                        "Property",
                        d.propertyId ? (
                          <Link key="p" to={`/properties/${d.propertyId}`}>
                            {d.addressLine1}
                          </Link>
                        ) : (
                          "—"
                        ),
                      ],
                      ["Notes", d.notes],
                    ]}
                  />
                  <h3 className="h6 mt-2">Linked to</h3>
                  {d.links.length === 0 ? (
                    <p className="small text-muted mb-0">Not linked to any record.</p>
                  ) : (
                    <ul className="small mb-0">
                      {d.links.map((l) => (
                        <li key={`${l.entityType}-${l.entityId}`}>
                          {(ATTACHABLE_TYPES as readonly string[]).includes(l.entityType) ? (
                            <Link
                              to={`/${ENTITY_ROUTES[l.entityType]}/${l.entityId}`}
                              className="text-capitalize"
                            >
                              {l.entityType.replace("_", " ")}
                            </Link>
                          ) : (
                            l.entityType
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
                <AuditHistory entityType="document" entityId={d.id} />
              </Col>
            </Row>
          </>
        );
      }}
    </QueryState>
  );
}
